<template>
  <div :class="['excerpt', { 'is-ghost': isGhost }]">
    <ExcerptIcon class="icon" />
    <div>
      <FriedH4 v-if="isGhost">{{ t('cc_deleted_content_title') }}</FriedH4>
      <EditorContent :editor="editor" class="plain-text truncate" spellcheck="false" />
    </div>
  </div>
</template>

<script lang="ts">
import type { Ref } from 'vue';
import { computed, onBeforeUnmount, watch, onMounted, defineComponent, ref } from 'vue';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import {
  BulletListNode,
  HardBreakNode,
  HeadingNode,
  ListItemNode,
  MergeValueNode,
  OrderedListNode,
  ParagraphNode,
  ProsemirrorType,
  TextAlign,
  TextDocNode,
} from '@getaccept/editor-lib';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import { useI18n } from 'vue-i18n';
import ExcerptIcon from '../assets/excerpt.svg';

export default defineComponent({
  components: { ExcerptIcon, EditorContent },
  props: {
    content: {
      type: String,
      required: true,
    },
    isGhost: { type: Boolean },
  },
  setup(props) {
    const { t } = useI18n();
    const editor: Ref<TipTapEditor | null> = ref(null);

    const parsedText = computed(() => JSON.parse(props.content));

    watch(
      () => props.content,
      () => {
        editor.value.commands.setContent(parsedText.value);
      }
    );

    const initEditor = () => {
      editor.value = new TipTapEditor({
        content: parsedText.value,
        editable: false,
        enableInputRules: false,
        enablePasteRules: false,
        extensions: [
          TextDocNode,
          ParagraphNode,
          Text,
          Bold,
          Italic,
          Strike,
          Superscript,
          Underline,
          OrderedListNode,
          BulletListNode,
          ListItemNode,
          MergeValueNode,
          Link.configure({
            autolink: false,
            openOnClick: true,
            linkOnPaste: false,
          }),
          HeadingNode,
          HardBreakNode,
          TextAlign.configure({
            types: [
              ProsemirrorType.Paragraph,
              ProsemirrorType.Heading,
              ProsemirrorType.BulletList,
              ProsemirrorType.OrderedList,
            ],
          }),
        ],
      });
    };

    onMounted(() => {
      initEditor();
    });

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    return { editor, t };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/lib-shared/src/assets/scss/truncate-text';
/* stylelint-disable selector-class-pattern */

.excerpt {
  background: var(--wood-87);
  padding: var(--spacing-100);
  display: flex;
  gap: var(--spacing-100);
  align-items: center;
  border-radius: var(--radius-small);
  height: 3.5rem;
  width: 100%;

  &.is-ghost {
    background: var(--gray-96);
    height: 5rem;
  }

  .icon {
    flex-shrink: 0;
  }

  .plain-text {
    :deep(.ProseMirror) > * {
      font-family: var(--default-font-family);
      font-size: var(--paragraph-medium-font-size);
      font-weight: var(--paragraph-medium-font-weight);
      line-height: var(--paragraph-medium-line-height);
      margin-block-start: 0;
      margin-block-end: 0;
    }
  }
}
</style>
